import React from "react";
import { theme } from "styles/theme";
import { Dealer } from "hooks/useDealers";
import AddDealerDialog from "components/users/AddDealerDialog";
import { ConfirmDialog } from "components/shared/ConfirmDialog";

interface DealersDialogsProps {
  addDealerDialogVisible: boolean;
  onHideAddDealer: () => void;

  deleteDealerDialogVisible: boolean;
  selectedDealer: Dealer | null;
  isDeleting: boolean;
  onHideDeleteDealer: () => void;
  onConfirmDelete: () => void;

  enableDealerDialogVisible: boolean;
  isEnabling: boolean;
  onHideEnableDealer: () => void;
  onConfirmEnable: () => void;

  disableDealerDialogVisible: boolean;
  isDisabling: boolean;
  onHideDisableDealer: () => void;
  onConfirmDisable: () => void;
}

function dealerFullName(dealer: Dealer | null) {
  if (!dealer) return "";
  return `${dealer.firstName} ${dealer.lastName}`;
}

const DealersDialogs: React.FC<DealersDialogsProps> = ({
  addDealerDialogVisible,
  onHideAddDealer,
  deleteDealerDialogVisible,
  selectedDealer,
  isDeleting,
  onHideDeleteDealer,
  onConfirmDelete,
  enableDealerDialogVisible,
  isEnabling,
  onHideEnableDealer,
  onConfirmEnable,
  disableDealerDialogVisible,
  isDisabling,
  onHideDisableDealer,
  onConfirmDisable,
}) => {
  const name = dealerFullName(selectedDealer);

  return (
    <>
      <AddDealerDialog
        visible={addDealerDialogVisible}
        onHide={onHideAddDealer}
      />

      <ConfirmDialog
        visible={deleteDealerDialogVisible && !!selectedDealer}
        title="Confirm Delete"
        message={`Are you sure you want to delete the dealer account for ${name}? This action cannot be undone. All data associated with this dealer will be permanently deleted.`}
        icon={{ className: "pi pi-trash", color: theme.colors.error }}
        onHide={onHideDeleteDealer}
        onConfirm={onConfirmDelete}
        isProcessing={isDeleting}
        confirmButtonLabel="Delete"
        confirmButtonClassName="p-button-danger"
        cancelButtonLabel="Cancel"
      />

      <ConfirmDialog
        visible={enableDealerDialogVisible && !!selectedDealer}
        title="Confirm Enable"
        message={`Are you sure you want to enable the dealer account for ${name}? This will allow the dealer to log in and access the system.`}
        icon={{ className: "pi pi-check-circle", color: theme.colors.success }}
        onHide={onHideEnableDealer}
        onConfirm={onConfirmEnable}
        isProcessing={isEnabling}
        confirmButtonLabel="Enable"
        confirmButtonClassName="p-button-success"
        cancelButtonLabel="Cancel"
      />

      <ConfirmDialog
        visible={disableDealerDialogVisible && !!selectedDealer}
        title="Confirm Disable"
        message={`Are you sure you want to disable the dealer account for ${name}? This will prevent the dealer from logging into the system. All associated data will be preserved.`}
        icon={{ className: "pi pi-ban", color: theme.colors.warning }}
        onHide={onHideDisableDealer}
        onConfirm={onConfirmDisable}
        isProcessing={isDisabling}
        confirmButtonLabel="Disable"
        confirmButtonClassName="p-button-secondary"
        cancelButtonLabel="Cancel"
      />
    </>
  );
};

export default DealersDialogs;
